import clsx from 'clsx';

import { Icon } from '@gitbook/spine-icons';
import { Badge, type BadgeProps, Tooltip } from '@gitbook/spine-react';

/**
 * Badge for a feature covered by an active trial.
 */
export function TrialingBadge(props: { plan: string } & Omit<BadgeProps, 'kind' | 'label'>) {
    const badgeProps = getTrialingBadgeProps(props);

    return (
        <Tooltip tooltip={getTrialingTooltip(props.plan)}>
            <Badge {...badgeProps} title={undefined} />
        </Tooltip>
    );
}

/**
 * Return the explanation shown when hovering a feature available during a trial.
 */
export function getTrialingTooltip(plan: string): string {
    return `Included during your trial. After it ends, you'll need ${plan} to keep using this feature.`;
}

/**
 * Return the shared badge props for a feature covered by an active trial.
 */
export function getTrialingBadgeProps({
    plan,
    className,
    ...props
}: { plan: string } & Omit<BadgeProps, 'kind' | 'label'>): BadgeProps {
    return {
        ...props,
        kind: 'upgrade',
        label: 'Trialing',
        title: getTrialingTooltip(plan),
        icon: Icon.Upgrade,
        className: clsx('cursor-default', className),
    };
}

/**
 * Returns the trial duration by looking at the number of days left.
 * If the trial has ended, it will display a message indicating that.
 * If the trial ends today, it will indicate that.
 * Otherwise, it will show the number of days left until the trial ends.
 */
export function getTrialEndingNotice(props: {
    daysLeft: number | undefined;
    prefix?: string;
}): string {
    const { daysLeft, prefix = '' } = props;
    const prefixStr = prefix ? `${prefix} ` : '';
    if (daysLeft === undefined || daysLeft === 0) {
        return `Your ${prefixStr}trial has ended`;
    }

    if (daysLeft === 1) {
        return `Your ${prefixStr}trial ends today`;
    }

    return `Your ${prefixStr}trial ends in ${daysLeft} days`;
}
